import { UserState } from './user.reducer';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import {
  InternalRoleTypes,
  OrganizationRoleTypes,
  OrganizationRoleDescriptions,
} from './user.enum';
import { OrganizationTypes, orgTypesWithLimitedAccess } from '../../shared/enums/organization.enum';
import { getHighestRole } from '../account-team-user/helpers/get-highest-role';
import { isFeatureFlagEnabled } from '../feature-flags/feature-flags.selectors';
import { organizationSubscriptionFeature } from '../organization-subscription/organization-subscription.feature';

const getUserState = createFeatureSelector<UserState>('user');

const getUserOrganizationRoles = createSelector(getUserState, (state) => state.organizationRoles);

export const getUserName = createSelector(getUserState, (state) => ({
  familyName: state.familyName,
  givenName: state.givenName,
}));

export const getCombinedUserName = createSelector(getUserName, ({ givenName, familyName }) =>
  [givenName, familyName].join(' ').trim(),
);

export const getUserId = createSelector(getUserState, (state) => state.id);

export const getOrganizationId = createSelector(getUserState, (state) => state.organizationId);

export const getOrganizationConfig = createSelector(
  getUserState,
  (state) => state.organizationConfig,
);

export const getDeploymentSubdomain = createSelector(
  getOrganizationConfig,
  (orgConfig) => orgConfig?.deploymentSubdomain,
);

export const getOrganizationType = createSelector(getUserState, (state) => state.organizationType);

export const getOrganizationName = createSelector(getUserState, (state) => state.organizationName);

export const getActiveOrganizationDisplayName = createSelector(
  getUserState,
  (state) => state.organizationDisplayName,
);

export const getOrganizationAccesses = createSelector(getUserState, (state) =>
  Object.values(state.organizationAccesses),
);

export const getOrganizationAccessesInfo = createSelector(
  getOrganizationAccesses,
  (organizationAccesses) => ({
    organizationAccesses: organizationAccesses.sort((a, b) =>
      a.organizationDisplayName.localeCompare(b.organizationDisplayName),
    ),
    hasOtherOrganizationAccesses: organizationAccesses.length > 1,
  }),
);

export const getIsOrganizationType = (type: OrganizationTypes) =>
  createSelector(getOrganizationType, (organizationType) => organizationType === type);

export const getUserRequestError = createSelector(getUserState, (state) => state.apiError);

export const organizationRoles = [
  {
    text: OrganizationRoleTypes.Editor,
    description: OrganizationRoleDescriptions.Editor,
    value: OrganizationRoleTypes.Editor,
  },
  {
    text: OrganizationRoleTypes.Owner,
    description: OrganizationRoleDescriptions.Owner,
    value: OrganizationRoleTypes.Owner,
  },
  {
    text: OrganizationRoleTypes.Support,
    description: OrganizationRoleDescriptions.Support,
    value: OrganizationRoleTypes.Support,
  },
  {
    text: OrganizationRoleTypes.Analytics,
    description: OrganizationRoleDescriptions.Analytics,
    value: OrganizationRoleTypes.Analytics,
  },
];

export const selectUserHighestRole = createSelector(getUserOrganizationRoles, getHighestRole);

export const getRoleDescription = createSelector(selectUserHighestRole, (currentRole) =>
  organizationRoles.find((role) => role.value === currentRole),
);

export const isOrganizationOwner = createSelector(getUserOrganizationRoles, (roles) =>
  roles.includes(OrganizationRoleTypes.Owner),
);

export const isOrganizationSupport = createSelector(getUserOrganizationRoles, (roles) =>
  roles.includes(OrganizationRoleTypes.Support),
);

export const selectUserHasAnalyticsRole = createSelector(getUserOrganizationRoles, (roles) =>
  roles.includes(OrganizationRoleTypes.Analytics),
);

export const selectUserCanManageSubscription = createSelector(
  isOrganizationOwner,
  (isOwner) => isOwner,
);

export const isOrganizationEditor = createSelector(
  getUserOrganizationRoles,
  (roles) => !roles.length,
);

export const isOrganizationOwnerOrSupport = createSelector(
  isOrganizationSupport,
  isOrganizationOwner,
  (isSupport, isOwner) => isSupport || isOwner,
);

export const canSeeAllProjects = createSelector(
  isOrganizationSupport,
  isOrganizationOwner,
  selectUserHasAnalyticsRole,
  (isSupport, isOwner, isAnalytics) => isSupport || isOwner || isAnalytics,
);

const templatesPermission: Record<OrganizationRoleTypes, boolean> = {
  [OrganizationRoleTypes.Owner]: true,
  [OrganizationRoleTypes.Editor]: true,
  [OrganizationRoleTypes.Support]: true,
  [OrganizationRoleTypes.Analytics]: false,
};

export const getHasTemplatesPermission = createSelector(
  selectUserHighestRole,
  (role) => templatesPermission[role],
);

export const isAdmin = createSelector(getUserState, (state) =>
  state.roles.includes(InternalRoleTypes.Admin),
);

export const userRoles = createSelector(getUserState, (state) => ({
  orgRoles: state.organizationRoles,
  internalRoles: state.roles,
}));

export const getSpecificUserRoles = createSelector(userRoles, ({ orgRoles, internalRoles }) => ({
  isAdmin: internalRoles.includes(InternalRoleTypes.Admin),
  isOwner: orgRoles.includes(OrganizationRoleTypes.Owner),
  isSupport: orgRoles.includes(OrganizationRoleTypes.Support),
  isAnalytics: orgRoles.includes(OrganizationRoleTypes.Analytics),
}));

export const isUserLoaded = createSelector(getUserState, (state) => Boolean(state.id));

export const getOrganizationProfile = createSelector(
  getUserState,
  (state) => state.organizationProfile,
);

export const getOrganizationDisplayName = createSelector(
  getOrganizationProfile,
  (organizationProfile) => organizationProfile.displayName,
);

export const getOrganizationHasLimitedAccess = createSelector(getOrganizationType, (type) =>
  orgTypesWithLimitedAccess.includes(type),
);

export const getOrganizationHasFullAccess = createSelector(
  getOrganizationType,
  (type) => !orgTypesWithLimitedAccess.includes(type),
);

export const getOrganizationCanManageSubscription = createSelector(
  getOrganizationType,
  (type: OrganizationTypes) =>
    [
      OrganizationTypes.Sandbox,
      OrganizationTypes.Basic,
      OrganizationTypes.Plus2,
      OrganizationTypes.Pro2,
      OrganizationTypes.Basic3,
      OrganizationTypes.Plus3,
      OrganizationTypes.Pro3,
      OrganizationTypes.ProPremium,
      OrganizationTypes.Elite,
    ].includes(type),
);

export const selectManageSubscription = createSelector(
  getOrganizationCanManageSubscription,
  selectUserCanManageSubscription,
  organizationSubscriptionFeature.selectSubscription,
  (organizationCanManageSubscription, userCanManageSubscription, selectSubscription) => ({
    organizationCanManageSubscription,
    userCanManageSubscription,
    selectSubscription,
  }),
);

// Insights feature is disabled: always return false
export const getHasInsightsAccess = createSelector(getUserState, () => false);

export const getCanConfigureOrchestrationServer = getOrganizationHasFullAccess;

export const getHasDesignerStylizedAccess = createSelector(
  getIsOrganizationType(OrganizationTypes.Internal),
  isFeatureFlagEnabled('blender-stylized-mode'),
  (isInternalOrganization, stylizedFeatureEnabled) =>
    isInternalOrganization || stylizedFeatureEnabled,
);

// Replaces LD flag: dashboard-conversation-minutes-usage-indicator
// Shows conversation minutes usage indicator for these subscription types
export const getShowConversationMinutesIndicator = createSelector(
  getOrganizationType,
  (type: OrganizationTypes) =>
    [
      OrganizationTypes.Basic,
      OrganizationTypes.Plus2,
      OrganizationTypes.Internal,
      OrganizationTypes.Pro2,
      OrganizationTypes.Basic3,
      OrganizationTypes.Plus3,
      OrganizationTypes.Pro3,
      OrganizationTypes.ProPremium,
      OrganizationTypes.ProCustom,
    ].includes(type),
);
